import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { FilePlus2, List, BarChart3, History, Mail, Users as UsersIcon, LogOut } from 'lucide-react';

import { signOut } from '../auth/msal';
import { config, isProduction } from '../config';
import { EMAIL_SECTION_ENABLED } from '../featureFlags';
import type { CurrentUser } from '../api/types';
import { TimezoneBadge } from './TimezoneBadge';

const navLinkClass = ({ isActive }: { isActive: boolean }) => `nav-item${isActive ? ' active' : ''}`;

export function AppShell({ user, children }: { user: CurrentUser; children: ReactNode }) {
  return (
    <div className="shell">
      <TimezoneBadge />
      <nav className="sidebar" aria-label="Main">
        <div className="sidebar-head">
          <div className="sidebar-wordmark">
            HOME<span>PRO</span>
          </div>
          <div className="sidebar-subtitle">Complaints Manager</div>
        </div>

        <div className="user-chip">
          {user.display_name}
          <span className="role">{user.role === 'admin' ? 'Administrator' : 'Associate'}</span>
        </div>

        <NavLink to="/complaints/new" className={navLinkClass}>
          <FilePlus2 size={16} aria-hidden /> New complaint
        </NavLink>
        <NavLink to="/complaints" end className={navLinkClass}>
          <List size={16} aria-hidden /> All complaints
        </NavLink>
        <NavLink to="/reports" className={navLinkClass}>
          <BarChart3 size={16} aria-hidden /> Reports
        </NavLink>
        <NavLink to="/audit" className={navLinkClass}>
          <History size={16} aria-hidden /> Audit log
        </NavLink>
        {user.permissions.manage_users && (
          <NavLink to="/users" className={navLinkClass}>
            <UsersIcon size={16} aria-hidden /> Users
          </NavLink>
        )}
        {EMAIL_SECTION_ENABLED && user.permissions.manage_email_config && (
          <NavLink to="/email" className={navLinkClass}>
            <Mail size={16} aria-hidden /> Email
          </NavLink>
        )}

        <button type="button" className="nav-item signout" onClick={() => void signOut()}>
          <LogOut size={16} aria-hidden /> Sign out
        </button>
      </nav>

      <main className="main">
        {!isProduction && (
          <div className="env-banner">
            {config.environmentName} environment — not production data
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
